/**
 * Ventutto strict, vendor-aware storefront search for Empire 12.
 */
(function () {
  'use strict';

  var config = window.VentuttoSearchConfig || {};
  var vendors = Array.isArray(config.vendors) ? config.vendors : [];
  var predictiveFields = 'title,vendor,product_type,variants.title,tag';

  function words(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(function (word) { return word && word !== 'and'; });
  }

  function distance(a, b) {
    var previous = Array.from({ length: b.length + 1 }, function (_, i) { return i; });

    for (var i = 1; i <= a.length; i += 1) {
      var current = [i];
      for (var j = 1; j <= b.length; j += 1) {
        current[j] = Math.min(
          current[j - 1] + 1,
          previous[j] + 1,
          previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
      previous = current;
    }

    return previous[b.length];
  }

  function closeWord(queryWord, vendorWord) {
    if (queryWord === vendorWord) return true;
    if (queryWord.endsWith('s') && queryWord.slice(0, -1) === vendorWord) return true;
    return queryWord.length >= 5 && vendorWord.length >= 5 && distance(queryWord, vendorWord) <= 1;
  }

  function uniqueVendorNames(names) {
    var seen = {};
    return names.filter(function (name) {
      var key = name.toLowerCase();
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function resolveVendors(query) {
    var queryWords = words(query);
    if (!queryWords.length) return { names: [], consumed: 0 };

    var fullMatches = vendors.filter(function (vendor) {
      var vendorWords = words(vendor);
      if (!vendorWords.length || vendorWords.length > queryWords.length) return false;
      return vendorWords.every(function (word, index) {
        return closeWord(queryWords[index], word);
      });
    });

    if (fullMatches.length) {
      var longest = Math.max.apply(null, fullMatches.map(function (vendor) {
        return words(vendor).length;
      }));
      var longestMatches = fullMatches.filter(function (vendor) {
        return words(vendor).length === longest;
      });

      if (longest > 1) {
        return { names: uniqueVendorNames(longestMatches), consumed: longest };
      }
    }

    // A one-word family search such as Versace (or a close typo such as
    // versance) can represent both Versace and Versace Jeans Couture.
    var familyMatches = vendors.filter(function (vendor) {
      var vendorWords = words(vendor);
      return vendorWords.length && closeWord(queryWords[0], vendorWords[0]);
    });

    return {
      names: uniqueVendorNames(familyMatches),
      consumed: familyMatches.length ? 1 : 0
    };
  }

  function visibleTerms(query) {
    return String(query || '')
      .replace(/\s+(AND|OR|NOT)\s+/gi, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function strictTerms(query) {
    var tokens = visibleTerms(query)
      .match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\S+/g) || [];

    tokens = tokens.filter(function (token) { return /[A-Za-z0-9]/.test(token); });
    return tokens.length > 1 ? tokens.join(' AND ') : (tokens[0] || '');
  }

  function escapeValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function titleQuery(searchWords) {
    var families = {
      shoe: ['shoe', 'sneaker', 'heel', 'pump', 'boot', 'sandal', 'flat', 'loafer', 'oxford', 'derby', 'slipper', 'slide'],
      shoes: ['shoe', 'sneaker', 'heel', 'pump', 'boot', 'sandal', 'flat', 'loafer', 'oxford', 'derby', 'slipper', 'slide'],
      heel: ['heel', 'pump'],
      heels: ['heel', 'pump'],
      pump: ['heel', 'pump'],
      pumps: ['heel', 'pump'],
      shirt: ['shirt', 'tee'],
      shirts: ['shirt', 'tee'],
      tee: ['shirt', 'tee'],
      tees: ['shirt', 'tee'],
      eyewear: ['sunglass', 'eyeglass', 'optical'],
      bag: ['bag', 'handbag', 'tote', 'clutch', 'backpack', 'satchel'],
      bags: ['bag', 'handbag', 'tote', 'clutch', 'backpack', 'satchel'],
      tie: ['tie', 'necktie'],
      ties: ['tie', 'necktie']
    };

    return searchWords.map(function (word) {
      var normalized = words(word)[0] || '';
      // Scarf has an irregular plural; preserve strict title matching for both.
      if ((normalized === 'scarf' || normalized === 'scarves') && words(word).length === 1) {
        return '(title:"scarf" OR title:"scarves")';
      }
      var family = families[normalized];
      if (family) {
        return '(' + family.map(function (term) {
          return 'title:' + term + '*';
        }).join(' OR ') + ')';
      }
      return 'title:"' + escapeValue(word) + '"';
    }).join(' AND ');
  }

  function buildQuery(query) {
    var naturalQuery = visibleTerms(query);
    if (!naturalQuery) return '';
    if (/(?:^|\s)[a-z_]+:/i.test(naturalQuery)) return query;

    var matched = resolveVendors(naturalQuery);
    var naturalWords = words(naturalQuery);

    if (!matched.names.length) {
      var rawTokens = naturalQuery.match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\S+/g) || [];
      rawTokens = rawTokens.filter(function (token) { return /[A-Za-z0-9]/.test(token); });
      return titleQuery(rawTokens);
    }

    var vendorClauses = matched.names.map(function (vendor) {
      return 'vendor:"' + escapeValue(vendor) + '"';
    });
    var vendorQuery = vendorClauses.length === 1
      ? vendorClauses[0]
      : '(' + vendorClauses.join(' OR ') + ')';

    var remainingWords = naturalWords.slice(matched.consumed);
    return remainingWords.length
      ? vendorQuery + ' AND ' + titleQuery(remainingWords)
      : vendorQuery;
  }

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    try {
      var action = new URL(form.action, window.location.origin);
      var path = new URL(window.Theme.routes.search_url, window.location.origin).pathname;
      return action.origin === window.location.origin && action.pathname === path;
    } catch (error) {
      return false;
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSearchForm(form)) return;
    var input = form.querySelector('input[name="q"]');
    if (input) {
      var original = visibleTerms(input.value);
      var technical = buildQuery(original);
      try {
        sessionStorage.setItem('ventutto_search_display', JSON.stringify({
          original: original,
          technical: technical
        }));
      } catch (error) {
        // Search remains functional when session storage is unavailable.
      }
      input.value = technical;
    }
  }, true);

  function restoreDisplayTerms() {
    try {
      var saved = JSON.parse(sessionStorage.getItem('ventutto_search_display'));
      var submitted = new URLSearchParams(window.location.search).get('q');
      if (!saved || submitted !== saved.technical) return;

      document.querySelectorAll('input[name="q"]').forEach(function (input) {
        input.value = saved.original;
        input.setAttribute('value', saved.original);
      });
      document.title = document.title.replace(/"[^"]*"/, '"' + saved.original + '"');
    } catch (error) {
      // Customer-facing restoration is optional; never interrupt search.
    }
  }

  // This asset loads at the end of the body, after Empire's search fields are
  // present. Restore immediately to avoid a visible technical-query flash,
  // then run once more at DOM ready as a safe fallback.
  restoreDisplayTerms();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreDisplayTerms, { once: true });
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var requestUrl = typeof input === 'string' ? input : input && input.url;
      if (!requestUrl) return nativeFetch(input, init);

      try {
        var url = new URL(requestUrl, window.location.origin);
        var path = new URL(window.Theme.routes.predictive_search_url, window.location.origin).pathname;
        if (url.origin === window.location.origin && url.pathname === path) {
          url.searchParams.set('q', buildQuery(url.searchParams.get('q')));
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[options][fields]', predictiveFields);
          input = typeof input === 'string' ? url.toString() : new Request(url.toString(), input);
        }
      } catch (error) {
        // Preserve Empire's original request if it cannot be decorated safely.
      }

      return nativeFetch(input, init);
    };
  }
}());
